import React, { useEffect } from 'react'
import { Map } from 'immutable'
import { connect as connectRedux } from 'react-redux'
import ImmutablePropTypes from 'react-immutable-proptypes'
import PropTypes from 'prop-types'
import compose from 'recompose/compose'
import { getBoundActions } from '../../../actions/index'
import { getPrimary } from '../../../services/languages/index'
import {
  URL_ACCOUNT_CHANGE_PLAN,
  URL_ACCOUNT,
} from '../../../services/url/constants'
import {
  getEmailCustomerServiceUrl,
} from '../../../services/url/index'
import { TARGET_BLANK } from '../../Link/constants'
import {
  getDateLocale,
  getDateTime,
  ordinalDateFormatingi18n,
  formatWithLocale,
} from '../../../services/date-time/index'
import { connect as connectPage, PAGE_SEO_TYPE_LOCATION } from '../../Page/connect'
import Sherpa, { TYPE_SMALL_WHITE } from '../../Sherpa/index'
import Link from '../../Link/index'
import Support from '../../Support/index'
import Button, { TYPES } from '../../Button/index'
import { H1, HEADING_TYPES } from '../../Heading/index'
import { getAuthIsLoggedIn } from '../../../services/auth/index'
import IconV2, { ICON_TYPES } from '../../Icon.v2/index'
import { scrollToTop } from '../../../services/dom/index'
import { CANCEL_FLOW_REACTIVATE_CLICKED_GA4_EVENT } from '../../../services/event-tracking/index'
import AccountCancelDissatisfied from '../AccountCancelDissatisfied'

function AccountCancelConfirmPage (props) {
  const {
    staticText,
    userAccount,
    user,
    auth,
    setUserAccountCancel,
    resetUserAccountManageSubscriptionData,
    resetFormSubmissionsData,
    setEventDefault,
    sendOptimizelyFeatureEvent,
  } = props
  const subscriptionExpireDate = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'paidThroughDate'])
  const formattedExpireDate = getDateTime(subscriptionExpireDate)
  const userLanguage = getPrimary(user.getIn(['data', 'language']))
  const dateFormatString = ordinalDateFormatingi18n(userLanguage)
  const dateLocale = getDateLocale(userLanguage)
  const processing = userAccount.get('billingSubscriptionsProcessing')
    || userAccount.getIn(['manageSubscription', 'data', 'cancelConfirmProcessing'])
    || userAccount.getIn(['details', 'data', 'billing', 'subscriptions'], Map()).size === 0
  const expireDate = formatWithLocale(formattedExpireDate, dateFormatString, { locale: dateLocale })
  const success = userAccount.getIn(['manageSubscription', 'data', 'cancelConfirmData', 'success'])
  const accountCancelReason = userAccount.getIn(['manageSubscription', 'data', 'cancelReason'])

  // Clean up on unmount; fire Optimizely event when confirm page loads
  useEffect(() => {
    scrollToTop()
    sendOptimizelyFeatureEvent({ eventName: 'cancel_flow_confirmed' })
    return () => {
      resetUserAccountManageSubscriptionData()
      resetFormSubmissionsData()
    }
  }, [])

  if (!getAuthIsLoggedIn(auth) || !accountCancelReason) {
    return null
  }

  return (
    <div className="account-cancel-confirm-page">
      <div className="account-cancel-confirm-page__content">
        {
          processing
            ? <Sherpa type={TYPE_SMALL_WHITE} />
            : null
        }
        {
          !processing && success === false
            ? <React.Fragment>
              <H1 as={HEADING_TYPES.H3} className="account-cancel-confirm-page__title">
                {staticText.getIn(['data', 'accountCancelConfirmError'])}
              </H1>
              <p className="account-cancel-confirm-page__body-text">
                {`${staticText.getIn(['data', 'errorDetails'])} `}
                <Link
                  to={getEmailCustomerServiceUrl(userLanguage)}
                  directLink
                  target={TARGET_BLANK}
                >
                  {staticText.getIn(['data', 'errorLink'])}
                </Link>
                .
              </p>
              <div className="account-cancel-confirm-page__buttons">
                <Button
                  type={TYPES.PRIMARY}
                  className="account-cancel-confirm-page__button"
                  onClick={setUserAccountCancel}
                >
                  {staticText.getIn(['data', 'retry'])}
                </Button>
              </div>
              <div className="account-cancel-confirm-page__links">
                <Link
                  className="account-cancel-confirm-page__link"
                  to={URL_ACCOUNT}
                >
                  {staticText.getIn(['data', 'goToAccount'])}
                  <IconV2
                    className="account-cancel-confirm-page__arrow-right"
                    type={ICON_TYPES.CHEVRON_RIGHT}
                  />
                </Link>
              </div>
            </React.Fragment>
            : null
        }
        {
          !processing && success !== false
            ? <React.Fragment>
                <H1 as={HEADING_TYPES.H3} className="account-cancel-confirm-page__title">
                  {staticText.getIn(['data', 'accountCancelConfirmTitle2'])}
                </H1>
                <p className="account-cancel-confirm-page__body-text">
                  {staticText.getIn(['data', 'accountCancelConfirmParagraphOne'])}
                  <strong>{` ${expireDate}.`}</strong>
                </p>
                <p className="account-cancel-confirm-page__body-text">
                  {staticText.getIn(['data', 'accountCancelConfirmParagraphTwo'])}
                </p>
                <div className="account-cancel-confirm-page__links">
                  <Link
                    className="account-cancel-confirm-page__link"
                    to={URL_ACCOUNT_CHANGE_PLAN}
                    onClick={() => {
                      setEventDefault(CANCEL_FLOW_REACTIVATE_CLICKED_GA4_EVENT)
                    }}
                  >
                    {staticText.getIn(['data', 'accountCancelConfirmLink'])}
                    <IconV2
                      className="account-cancel-confirm-page__arrow-right"
                      type={ICON_TYPES.CHEVRON_RIGHT}
                    />
                  </Link>
                </div>
                <AccountCancelDissatisfied useLegacyLayout />
              </React.Fragment>
            : null
        }
      </div>
      <Support />
    </div>
  )
}

AccountCancelConfirmPage.propTypes = {
  auth: ImmutablePropTypes.map.isRequired,
  user: ImmutablePropTypes.map.isRequired,
  userAccount: ImmutablePropTypes.map.isRequired,
  staticText: ImmutablePropTypes.map.isRequired,
  setUserAccountCancel: PropTypes.func.isRequired,
  resetUserAccountManageSubscriptionData: PropTypes.func.isRequired,
  resetFormSubmissionsData: PropTypes.func.isRequired,
  sendOptimizelyFeatureEvent: PropTypes.func.isRequired,
}

export default compose(
  connectPage({ seoType: PAGE_SEO_TYPE_LOCATION }),
  connectRedux(
    (state) => ({
      staticText: state.staticText.getIn(['data', 'accountCancelConfirm']),
      user: state.user,
      userAccount: state.userAccount,
      auth: state.auth,
    }),
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        setUserAccountCancel: actions.userAccount.setUserAccountCancel,
        resetUserAccountManageSubscriptionData:
        actions.userAccount.resetUserAccountManageSubscriptionData,
        resetFormSubmissionsData: actions.formSubmissions.resetFormSubmissionsData,
        setEventDefault: actions.eventTracking.setEventDefault,
        sendOptimizelyFeatureEvent: actions.optimizelyFeature.sendOptimizelyFeatureEvent,
      }
    },
  ),
)(AccountCancelConfirmPage)
